import { PropsWithChildren } from 'react'

import PermissionsContext from './PermissionsContext'
import { useStore } from '@ors/store'

interface PermissionsProviderProps extends PropsWithChildren {}

const PermissionsProvider = (props: PermissionsProviderProps) => {
  const { children } = props

  const commonSlice = useStore((state) => state.common)
  const user_permissions = commonSlice.user_permissions.data || []
  const user_permissions_as_set = new Set(user_permissions)

  const canViewCPReports = user_permissions.includes(
    'has_cp_report_view_access',
  )
  const canEditCPReports = user_permissions.includes(
    'has_cp_report_edit_access',
  )
  const canSubmitFinalCPReport = user_permissions.includes(
    'can_submit_final_cp_version',
  )
  const canDeleteCPReports = user_permissions.includes(
    'has_cp_report_delete_access',
  )
  const canExportCPReports = user_permissions.includes(
    'has_cp_report_export_access',
  )

  const canViewBp = user_permissions.includes('has_business_plan_view_access')
  const canUpdateBp = user_permissions.includes('has_business_plan_edit_access')
  const isBpAdmin =
    canUpdateBp && user_permissions.includes('can_view_all_agencies')

  const canViewV1Projects = user_permissions.includes('has_project_view_access')

  const canViewProjects = user_permissions.includes(
    'has_project_v2_view_access',
  )
  const canViewMetainfoProjects = user_permissions.includes(
    'has_project_metainfo_view_access',
  )
  const canViewSectorsSubsectors = user_permissions.includes(
    'has_sectors_and_subsectors_view_access',
  )
  const canUpdateProjects = user_permissions.includes(
    'has_project_v2_edit_access',
  )
  const canUpdateV3Projects = user_permissions.includes(
    'has_project_v2_version3_edit_access',
  )
  const canSubmitProjects = user_permissions.includes(
    'has_project_v2_submit_access',
  )
  const canRecommendProjects = user_permissions.includes(
    'has_project_v2_recommend_projects_access',
  )
  const canApproveProjects = user_permissions.includes(
    'has_project_v2_approve_projects_access',
  )
  const canAssociateProjects = user_permissions.includes(
    'has_project_v2_associate_projects_access',
  )
  const canDisassociateProjects = user_permissions.includes(
    'has_project_v2_remove_association_access',
  )
  const canDisassociateComponents = user_permissions.includes(
    'has_project_v2_disassociate_component_access',
  )
  const canEditApprovedProjects = user_permissions.includes(
    'has_project_v2_edit_approved_access',
  )
  const canEditProjects =
    canViewProjects &&
    (canUpdateProjects ||
      canUpdateV3Projects ||
      canSubmitProjects ||
      canRecommendProjects ||
      canApproveProjects ||
      canEditApprovedProjects)
  const canViewProductionProjects = user_permissions.includes(
    'can_view_production_projects',
  )
  const canManageMyaData = user_permissions.includes(
    'has_project_v2_mya_manage_access',
  )
  const canViewMyaData =
    canManageMyaData ||
    user_permissions.includes('has_project_v2_mya_view_access')
  const canManageFundingWindow = user_permissions.includes(
    'has_project_v2_funding_window_manage_access',
  )
  const canViewFundingWindow =
    canManageFundingWindow ||
    user_permissions.includes('has_project_v2_funding_window_view_access')
  const canUpdatePostExcom = user_permissions.includes(
    'has_project_v2_edit_post_excom',
  )
  const canTransferProjects = user_permissions.includes(
    'has_project_v2_transfer_projects_access',
  )

  const canViewEnterprises = user_permissions.includes(
    'has_enterprise_view_access',
  )
  const canEditEnterprise = user_permissions.includes(
    'has_enterprise_edit_access',
  )

  const canSetProjectSettings = user_permissions.includes(
    'has_project_settings_access',
  )

  const isMlfsUser = user_permissions.includes('is_mlfs_user')
  const isSuperuser = user_permissions.includes('is_superuser')
  const canViewOnlyOwnAgency = user_permissions.includes(
    'can_view_only_own_agency',
  )

  const canCommentCPCountry = user_permissions.includes(
    'can_cp_country_type_comment',
  )

  const canCommentCPSecretariat = user_permissions.includes(
    'can_cp_secretariat_type_comment',
  )

  const isCPCountryUserType =
    user_permissions_as_set.has('can_view_only_own_country') &&
    user_permissions_as_set.has('has_cp_report_view_access') &&
    !user_permissions.includes('can_view_all_countries')

  const canViewReplenishment = user_permissions.includes(
    'has_replenishment_view_access',
  )
  const canEditReplenishment = user_permissions.includes(
    'has_replenishment_edit_access',
  )

  const canViewAPR = user_permissions.includes('has_apr_view_access')
  const canEditAPR = user_permissions.includes('has_apr_edit_access')
  const canSubmitAPR = user_permissions.includes('has_apr_submit_access')

  const canViewPCR = user_permissions.includes('has_pcr_view_access')
  const canEditPCR = user_permissions.includes('has_pcr_edit_access')
  const canSubmitPCR = user_permissions.includes('has_pcr_submit_access')
  const canEditSubmittedPCR = user_permissions.includes(
    'has_pcr_edit_submitted_access',
  )
  const canRequestAdjustmentsPCR = user_permissions.includes(
    'has_pcr_request_adjustments_access',
  )
  const canApprovePCR = user_permissions.includes('has_pcr_approve_access')
  const canMarkPCRDue = user_permissions.includes(
    'has_project_v2_pcr_mark_pcr_due_access',
  )

  return (
    <PermissionsContext.Provider
      value={{
        canViewCPReports,
        canEditCPReports,
        canSubmitFinalCPReport,
        canDeleteCPReports,
        canExportCPReports,
        canViewBp,
        canUpdateBp,
        canViewReplenishment,
        canEditReplenishment,
        canViewV1Projects,
        canViewProjects,
        canViewMetainfoProjects,
        canViewSectorsSubsectors,
        canUpdateProjects,
        canUpdateV3Projects,
        canSubmitProjects,
        canRecommendProjects,
        canApproveProjects,
        canAssociateProjects,
        canDisassociateProjects,
        canDisassociateComponents,
        canEditProjects,
        canEditApprovedProjects,
        canViewProductionProjects,
        canUpdatePostExcom,
        canTransferProjects,
        canViewMyaData,
        canManageMyaData,
        canViewFundingWindow,
        canManageFundingWindow,
        canViewEnterprises,
        canEditEnterprise,
        canSetProjectSettings,
        isMlfsUser,
        isSuperuser,
        canViewOnlyOwnAgency,
        canCommentCPCountry,
        canCommentCPSecretariat,
        isCPCountryUserType,
        isBpAdmin,
        canViewAPR,
        canEditAPR,
        canSubmitAPR,
        canViewPCR,
        canEditPCR,
        canSubmitPCR,
        canEditSubmittedPCR,
        canRequestAdjustmentsPCR,
        canApprovePCR,
        canMarkPCRDue,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  )
}

export default PermissionsProvider
